import { useState } from 'react';
import type { CSSProperties, JSX } from 'react';
import type { Palette } from '../../../../_theme/palette.js';
import { hexWithAlpha } from '../../../../_theme/palette.js';
import type { SurveyQuestion } from '../../types.js';

interface DropdownFieldProps {
  palette: Palette;
  question: SurveyQuestion;
  value: unknown;
  onChange: (value: string) => void;
}

const PREMIUM_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

// Native <select> styled to match the SDK's text inputs so dropdowns sit
// alongside other form controls without visual jolt. Native dropdown lets
// mobile use the OS picker (iOS wheel / Android sheet) — much better UX
// than a custom popover that fights the on-screen keyboard.
export function DropdownField({ palette, question, value, onChange }: DropdownFieldProps): JSX.Element {
  const options = question.settings?.options ?? [];
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <select
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => onChange(event.currentTarget.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-label={question.label}
        style={selectStyle(palette, focused, hovered)}
      >
        <option value="">{question.settings?.placeholder ?? 'Select an option'}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span aria-hidden="true" style={chevronStyle(palette)}>
        <ChevronIcon />
      </span>
    </div>
  );
}

function selectStyle(palette: Palette, focused: boolean, hovered: boolean): CSSProperties {
  const borderColor = focused
    ? palette.primary
    : hovered
      ? hexWithAlpha(palette.primary, 0.6)
      : palette.border;
  return {
    width: '100%',
    padding: '12px 36px 12px 14px',
    borderRadius: palette.radius,
    border: `1px solid ${borderColor}`,
    background: palette.card,
    color: palette.text,
    fontFamily: 'inherit',
    fontSize: 14,
    appearance: 'none',
    outline: 'none',
    boxSizing: 'border-box',
    transition: `border-color 220ms ${PREMIUM_EASE}, box-shadow 220ms ${PREMIUM_EASE}`,
    boxShadow: focused ? `0 0 0 4px ${hexWithAlpha(palette.primary, 0.14)}` : 'none',
    cursor: 'pointer',
  };
}

function chevronStyle(palette: Palette): CSSProperties {
  return {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: palette.textMuted,
    pointerEvents: 'none',
    display: 'flex',
  };
}

function ChevronIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
